import {useState, useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {Layers, Link2, Check, X, Edit, Plus, Trash2, CheckCircle, Archive, Tag} from 'lucide-react'
import {InlineAttributeEditor} from '@/components/attributes/InlineAttributeEditor'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardTitle, CardDescription} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {Badge} from '@/components/ui/badge'
import {RiskLevelBadge} from '@/components/ui/risk-level-badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {Label} from '@/components/ui/label'
import {AccessProfileStatus, AccessProfileBindingRequestFields} from '@/types/access-profile'
import type {
    AccessProfileBindingCreateRequest
} from '@/types/access-profile'
import {BadgeVariant} from '@/components/ui/badge'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {EMPTY_SEARCH_AND_FILTERS, type SearchAndFilters} from '@/hooks/use-server-pagination'
import type {SearchAndFilterContract} from '@/types/endpoint-contract'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {permissions} from '@/lib/permissions'
import {formatTimestamp} from '@/lib/date-utils'
import {ErrorBanner} from '@/components/ui/error-banner'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {PageLayout} from '@/components/shared/PageLayout'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {CardSection} from '@/components/shared/CardSection'
import {OwnersSection, OwnersEntityType} from '@/components/owners/OwnersSection'
import {Checkbox} from '@/components/ui/checkbox'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink} from '@/components/shared/EntityLink'
import {MonoText} from '@/components/shared/MonoText'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Icon} from '@/components/shared/Icon'
import {Skeleton} from '@/components/ui/skeleton'

const ENFORCEMENT_OBJECT_SEARCH_CONTRACT: SearchAndFilterContract = {
    sort: {},
    filter: {},
    search: {param: 'q'},
}

export function AccessProfileDetail() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.accessProfiles.detailPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{ id: string }>()
    const {hasPermission} = useAuth()

    const canUpdate = hasPermission(permissions.accessProfiles.update)
    const canDelete = hasPermission(permissions.accessProfiles.delete)
    const {
        accessProfileBindingOperationTypes: operationTypes,
        enforcementTypes,
        environmentTypes,
        accessProfileStatuses: profileStatuses,
        riskLevelTypes,
        functionGroupTypes: functionGroupTypesAP,
        accessProfileResponsibilityTypes,
    } = useAppConfig()

    // Dialog state
    const addBindingDialog = useDialog()
    const [deleteBindingId, setDeleteBindingId] = useState<string | null>(null)
    const deleteProfileDialog = useDialog()
    const activateDialog = useDialog()
    const deprecateDialog = useDialog()

    // Add binding form state
    const [selectedEnforcementObjectIds, setSelectedEnforcementObjectIds] = useState<Set<string>>(new Set())
    const [selectedOperation, setSelectedOperation] = useState('')
    const [enforcementObjectFilter, setEnforcementObjectFilter] = useState<SearchAndFilters>(EMPTY_SEARCH_AND_FILTERS)

    // Fetch access profile
    const {data: profile, isLoading, error} = useQuery({
        queryKey: ['access-profile', id],
        queryFn: () => services.accessProfiles.detail(id!),
        enabled: !!id,
    })

    // Fetch bindings
    const {data: bindings = []} = useQuery({
        queryKey: ['access-profile-bindings', id],
        queryFn: () => services.accessProfiles.bindings(id!),
        enabled: !!id,
    })

    // Fetch function groups associated with this access profile
    const {
        data: functionGroupsData = [],
        isLoading: fgLoading,
        error: fgError,
        refetch: fgRefetch,
    } = useQuery({
        queryKey: ['access-profile-function-groups', id],
        queryFn: () => services.accessProfiles.functionGroups(id!),
        enabled: !!id,
    })

    // Fetch all enforcement objects for the profile's resource (for binding selection)
    const {data: allEnforcementObjectsData, isLoading: enforcementObjectsLoading} = useQuery({
        queryKey: ['enforcement-objects', 'by-resource', profile?.resource.id],
        queryFn: () => {
            return services.enforcementObjects.list({
                ...(profile?.resource.id ? {resourceId: profile.resource.id} : {}),
                size: 100,
                sort: 'displayName,asc',
            })
        },
        enabled: !!profile?.resource.id && addBindingDialog.isOpen,
    })

    // Filter available enforcement objects (not yet bound) and by search filter
    const availableEnforcementObjects = useMemo(() => {
        const allObjects = allEnforcementObjectsData?.content || []
        const boundIds = new Set(bindings.map(b => b.enforcementObject.id))
        const notBound = allObjects.filter(eo => !boundIds.has(eo.id))

        if (!enforcementObjectFilter.q) return notBound

        const filterLower = enforcementObjectFilter.q.toLowerCase()
        return notBound.filter(eo =>
            eo.derivedDisplayName.toLowerCase().includes(filterLower) ||
            eo.externalIdentifier.toLowerCase().includes(filterLower) ||
            eo.targetSystem.displayName.toLowerCase().includes(filterLower) ||
            eo.enforcementType.toLowerCase().includes(filterLower)
        )
    }, [allEnforcementObjectsData, bindings, enforcementObjectFilter])

    // Get selected enforcement objects from the list
    const selectedEnforcementObjects = useMemo(() => {
        if (selectedEnforcementObjectIds.size === 0) return []
        return allEnforcementObjectsData?.content?.filter(eo => selectedEnforcementObjectIds.has(eo.id)) || []
    }, [allEnforcementObjectsData, selectedEnforcementObjectIds])

    // Add binding mutation
    const addBindingMutation = useEntityMutation({

        mutationFn: (data: Omit<AccessProfileBindingCreateRequest, 'accessProfileId'>) =>

            services.accessProfiles.addBinding(id!, {
                ...data,
                [AccessProfileBindingRequestFields.accessProfileId]: id!,
            }),
        invalidate: ['access-profile-bindings', id],
        successMessage: i18n.accessProfiles.bindingAdded,
        closeDialog: addBindingDialog.close,
        onSuccess: () => {
            resetAddBindingForm()
        },
    })

    // Remove binding mutation
    const removeBindingMutation = useEntityMutation({
        mutationFn: (bindingId: string) =>
            services.accessProfiles.removeBinding(bindingId),
        invalidate: ['access-profile-bindings', id],
        successMessage: i18n.accessProfiles.bindingRemoved,
        closeDialog: () => setDeleteBindingId(null),
    })

    // Activate profile mutation
    const activateMutation = useEntityMutation({
        mutationFn: () =>
            services.accessProfiles.activate(id!),
        invalidate: [['access-profile', id], ['access-profiles']],
        successMessage: i18n.accessProfiles.profileActivated,
        closeDialog: activateDialog.close,
    })

    // Deprecate profile mutation
    const deprecateMutation = useEntityMutation({
        mutationFn: () =>
            services.accessProfiles.deprecate(id!),
        invalidate: [['access-profile', id], ['access-profiles']],
        successMessage: i18n.accessProfiles.profileDeprecated,
        closeDialog: deprecateDialog.close,
    })

    // Delete profile mutation
    const deleteProfileMutation = useEntityMutation({
        mutationFn: () => services.accessProfiles.remove(id!),
        invalidate: ['access-profiles'],
        successMessage: i18n.accessProfiles.profileDeleted,
        onSuccess: () => {
            navigate(routes.accessGovernance.profiles.list())
        },
    })

    const resetAddBindingForm = () => {
        setSelectedEnforcementObjectIds(new Set())
        setSelectedOperation('')
        setEnforcementObjectFilter(EMPTY_SEARCH_AND_FILTERS)
    }

    const toggleEnforcementObjectSelection = (id: string) => {
        setSelectedEnforcementObjectIds(prev => {
            const newSet = new Set(prev)
            if (newSet.has(id)) {
                newSet.delete(id)
            } else {
                newSet.add(id)
            }
            return newSet
        })
    }

    const toggleSelectAll = () => {
        if (selectedEnforcementObjectIds.size === availableEnforcementObjects.length) {
            setSelectedEnforcementObjectIds(new Set())
        } else {
            setSelectedEnforcementObjectIds(new Set(availableEnforcementObjects.map(eo => eo.id)))
        }
    }

    const handleAddBinding = async () => {
        if (selectedEnforcementObjects.length === 0 || !selectedOperation) return

        // Create bindings for all selected enforcement objects
        for (const eo of selectedEnforcementObjects) {
            await addBindingMutation.mutateAsync({
                systemId: eo.targetSystem.id,
                enforcementObjectId: eo.id,
                operation: selectedOperation,
            })
        }
    }

    const handleRemoveBinding = async (bindingId: string) => {
        await removeBindingMutation.mutateAsync(bindingId)
    }

    const getStatusColor = (status: AccessProfileStatus) => {
        switch (status) {
            case AccessProfileStatus.ACTIVE:
                return BadgeVariant.SUCCESS
            case AccessProfileStatus.DEPRECATED:
                return BadgeVariant.SECONDARY
        }
    }



    return (
        <PageLayout
            variant="detail"
            title={profile?.name ?? i18n.accessProfiles.detailPageTitle}
            icon={CONCEPT_ICON.accessProfile}
            backTo={true}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(profile, 'ACCESS_PROFILE') : undefined)}
            data-testid="access-profile-detail-page"
            description={profile ? (
                <InlineRow>
                    <MonoText muted data-testid="profile-code">{profile.code}</MonoText>
                    <Badge variant={getStatusColor(profile.status)}>{labelOf(profileStatuses, profile.status)}</Badge>
                    <Badge variant="secondary">{labelOf(environmentTypes, profile.environment)}</Badge>
                    <RiskLevelBadge riskLevel={profile.riskLevel}/>
                </InlineRow>
            ) : undefined}
            actions={profile ? (
                <InlineRow>
                    {canUpdate && profile.status === AccessProfileStatus.DEPRECATED && (
                        <Button data-testid="btn-activate" variant="outline" onClick={activateDialog.open}>
                            <Icon icon={CheckCircle} className="mr-2"/>
                            {i18n.accessProfiles.activate}
                        </Button>
                    )}
                    {canUpdate && profile.status === AccessProfileStatus.ACTIVE && (
                        <Button data-testid="btn-deprecate" variant="outline" onClick={deprecateDialog.open}>
                            <Icon icon={Archive} className="mr-2"/>
                            {i18n.accessProfiles.deprecate}
                        </Button>
                    )}
                    {canUpdate && (
                        <Button data-testid="btn-edit" onClick={() => navigate(routes.accessGovernance.profiles.edit(id!))}>
                            <Icon icon={Edit} className="mr-2"/>
                            {i18n.common.edit}
                        </Button>
                    )}
                    {canDelete && (
                        <Button data-testid="btn-delete-profile" variant="destructive" onClick={deleteProfileDialog.open}>
                            <Icon icon={Trash2} className="mr-2"/>
                            {i18n.common.delete}
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {profile && <>
                {/* Profile Details */}
                <DetailsCard
                    title={i18n.accessProfiles.profileDetails}
                    description={i18n.accessProfiles.profileDetailsDesc}
                    columns={3}
                    data-testid="card-profile-details"
                    fields={[
                        ...(profile.description ? [{label: i18n.common.description, value: <span data-testid="profile-description">{profile.description}</span>, fullWidth: true as const}] : []),
                        {
                            label: i18n.accessProfiles.resource,
                            value: <EntityLink testId="link-resource" value={profile.resource}/>,
                        },
                        {label: i18n.accessProfiles.environment, value: labelOf(environmentTypes, profile.environment)},
                        {label: i18n.accessProfiles.riskLevel, value: labelOf(riskLevelTypes, profile.riskLevel)},
                        {label: i18n.accessProfiles.reviewFrequencyDays, value: i18n.accessProfiles.maxDurationDays({days: profile.reviewFrequencyDays})},
                        {
                            label: i18n.accessProfiles.requestable,
                            value: profile.requestable
                                ? <InlineRow gap="xs"><Icon icon={Check} className="text-status-success"/><span>{i18n.common.yes}</span></InlineRow>
                                : <InlineRow gap="xs"><Icon icon={X} muted/><span>{i18n.common.no}</span></InlineRow>,
                        },
                        {
                            label: i18n.accessProfiles.autoAssignable,
                            value: profile.autoAssignable
                                ? <InlineRow gap="xs"><Icon icon={Check} className="text-status-success"/><span>{i18n.common.yes}</span></InlineRow>
                                : <InlineRow gap="xs"><Icon icon={X} muted/><span>{i18n.common.no}</span></InlineRow>,
                        },
                        {
                            label: i18n.accessProfiles.expiryRequired,
                            value: profile.expiryRequired
                                ? <InlineRow gap="xs"><Icon icon={Check} className="text-status-success"/><span>{i18n.common.yes}</span></InlineRow>
                                : <InlineRow gap="xs"><Icon icon={X} muted/><span>{i18n.common.no}</span></InlineRow>,
                        },
                        ...(profile.maxDurationDays ? [{label: i18n.accessProfiles.maxDuration, value: i18n.accessProfiles.maxDurationDays({days: profile.maxDurationDays})}] : []),
                        {label: i18n.common.createdAt, value: formatTimestamp(profile.createdAt)},
                        {label: i18n.common.lastUpdated, value: formatTimestamp(profile.updatedAt)},
                    ]}
                />

                <OwnersSection
                    entityType={OwnersEntityType.accessProfiles}
                    entityId={id!}
                    canEdit={canUpdate}
                    responsibilityOptions={accessProfileResponsibilityTypes}
                />

                <CardSection data-testid="section-attributes" icon={Tag} title={i18n.common.attributes}>
                    <InlineAttributeEditor
                        entityType={AttributeEntityType.ACCESS_PROFILE}
                        entityId={id!}
                        entityTypeCode={profile.code}
                        canEdit={hasPermission(permissions.attributeDefinitions.apUpdate)}
                    />
                </CardSection>

                {/* Function Groups Card */}
                <CardSection
                    icon={Layers}
                    title={String(i18n.accessProfileDetail.functionGroups.card.title)}
                    description={fgLoading ? undefined : String(
                        i18n.accessProfileDetail.functionGroups.card.description({count: functionGroupsData.length})
                    )}
                    data-testid="ap-function-groups-card"
                >
                    {fgLoading && (
                        <Stack gap="sm">
                            {[0, 1, 2].map(i => (
                                <Skeleton key={i} className="h-10 w-full"/>
                            ))}
                        </Stack>
                    )}
                    {!fgLoading && fgError && (
                        <Stack gap="sm">
                            <ErrorBanner error={fgError}/>
                            <MutedText>{i18n.accessProfileDetail.functionGroups.error}</MutedText>
                            <Button variant="outline" size="sm" onClick={() => fgRefetch()} data-testid="btn-retry-fg">
                                {i18n.common.retry}
                            </Button>
                        </Stack>
                    )}
                    {!fgLoading && !fgError && (
                        functionGroupsData.length === 0 ? (
                            <MutedText block centered data-testid="fg-empty-state">
                                {String(i18n.accessProfileDetail.functionGroups.empty)}
                            </MutedText>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-fg-name">{i18n.accessProfileDetail.functionGroups.columns.name}</TableHead>
                                        <TableHead data-testid="col-fg-type">{i18n.accessProfileDetail.functionGroups.columns.type}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {functionGroupsData.map((fg) => (
                                        <TableRow key={fg.id} data-testid={`fg-row-${fg.functionGroup.id}`}>
                                            <TableCell>
                                                <TextLink
                                                    to={routes.accessGovernance.functionGroups.detail(fg.functionGroup.id)}
                                                    variant="bold"
                                                    testId={`link-fg-${fg.functionGroup.id}`}
                                                >
                                                    {fg.functionGroup.displayName}
                                                </TextLink>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {labelOf(functionGroupTypesAP, fg.functionGroup.subType ?? '')}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )
                    )}
                </CardSection>

                {/* Enforcement Objects */}
                <Card>
                    <RowCardHeader>
                        <div>
                            <CardTitle data-testid="card-title-enforcement-objects">
                                <InlineRow gap="sm">
                                    <Link2 className="h-5 w-5"/>
                                    {i18n.accessProfiles.enforcementObjects}
                                </InlineRow>
                            </CardTitle>
                            <CardDescription>
                                {i18n.accessProfiles.boundCount({count: bindings.length})}
                            </CardDescription>
                        </div>
                        {canUpdate && (
                            <Button data-testid="btn-add-enforcement-object" size="sm" onClick={addBindingDialog.open}>
                                <Icon icon={Plus} className="mr-2"/>
                                {i18n.accessProfiles.addEnforcementObject}
                            </Button>
                        )}
                    </RowCardHeader>
                    <CardContent>
                        {bindings.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead data-testid="col-enforcement-object">{i18n.accessProfiles.enforcementObjects}</TableHead>
                                        <TableHead data-testid="col-system">{i18n.accessProfiles.system}</TableHead>
                                        <TableHead data-testid="col-operation">{i18n.common.operation}</TableHead>
                                        {canUpdate && <TableHead className="w-[100px]" data-testid="col-actions">{i18n.common.actions}</TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {bindings.map((binding) => (
                                        <TableRow key={binding.id}>
                                            <TableCell>
                                                <EntityLink
                                                    testId={`link-enforcement-object-${binding.id}`}
                                                    value={binding.enforcementObject}
                                                />
                                                <MutedText size="sm" block data-testid={`enforcement-type-${binding.id}`}>
                                                    {labelOf(enforcementTypes, binding.enforcementType)}
                                                </MutedText>
                                            </TableCell>
                                            <TableCell>
                                                <EntityLink
                                                    testId={`link-system-${binding.id}`}
                                                    value={binding.targetSystem}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {labelOf(operationTypes, binding.operation)}
                                                </Badge>
                                            </TableCell>
                                            {canUpdate && (
                                                <TableCell>
                                                    <Button
                                                        data-testid={`btn-delete-binding-${binding.id}`}
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setDeleteBindingId(binding.id)}
                                                        className="text-destructive hover:text-destructive"
                                                    >
                                                        <Trash2 className="h-4 w-4"/>
                                                    </Button>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <MutedText block centered>{i18n.accessProfiles.noEnforcementObjects}</MutedText>
                        )}
                    </CardContent>
                </Card>

            {/* Add Binding Dialog */}
            <Dialog open={addBindingDialog.isOpen} onOpenChange={(open) => {
                addBindingDialog.bind.onOpenChange(open)
                if (!open) resetAddBindingForm()
            }}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>{i18n.accessProfiles.addBindings}</DialogTitle>
                        <DialogDescription>
                            {i18n.accessProfiles.addBindingsDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <Stack className="py-4">
                        {/* Filter input */}
                        <UnifiedSearchBar
                            contract={ENFORCEMENT_OBJECT_SEARCH_CONTRACT}
                            value={enforcementObjectFilter}
                            onChange={(next) => setEnforcementObjectFilter(prev => ({...prev, ...next}))}
                            placeholder={i18n.accessProfiles.filterEnforcementObjects as string}
                            testIdNamespace="enforcement-object-filter"
                        />

                        {/* Enforcement objects table */}
                        <Card>
                            {enforcementObjectsLoading ? (
                                <MutedText block className="py-8">{i18n.accessProfiles.loadingEnforcementObjects}</MutedText>
                            ) : availableEnforcementObjects.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-[40px]" data-testid="col-select">
                                                <Button
                                                    data-testid="btn-select-all-enforcement-objects"
                                                    type="button"
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={toggleSelectAll}
                                                    className="h-4 w-4"
                                                >
                                                    {selectedEnforcementObjectIds.size === availableEnforcementObjects.length && availableEnforcementObjects.length > 0 && (
                                                        <Check className="h-3 w-3"/>
                                                    )}
                                                </Button>
                                            </TableHead>
                                            <TableHead data-testid="col-eo-name">{i18n.common.name}</TableHead>
                                            <TableHead data-testid="col-eo-system">{i18n.accessProfiles.system}</TableHead>
                                            <TableHead data-testid="col-eo-type">{i18n.common.type}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {availableEnforcementObjects.map((eo) => {
                                            const selected = selectedEnforcementObjectIds.has(eo.id)
                                            return (
                                            <TableRow
                                                key={eo.id}
                                                className={selected ? 'cursor-pointer bg-muted' : 'cursor-pointer hover:bg-muted/50'}
                                                onClick={() => toggleEnforcementObjectSelection(eo.id)}
                                            >
                                                <TableCell className="text-center">
                                                    <Checkbox checked={selected} tabIndex={-1}/>
                                                </TableCell>
                                                <TableCell>
                                                    <Strong>{eo.derivedDisplayName}</Strong>
                                                    <MutedText size="sm">{eo.externalIdentifier}</MutedText>
                                                </TableCell>
                                                <TableCell>{eo.targetSystem.displayName}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">
                                                        {labelOf(enforcementTypes, eo.enforcementType)}
                                                    </Badge>
                                                </TableCell>
                                            </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            ) : (
                                <MutedText block className="py-8">
                                    {enforcementObjectFilter.q
                                        ? i18n.accessProfiles.noEnforcementObjectsMatch
                                        : i18n.accessProfiles.noAvailableEnforcementObjects}
                                </MutedText>
                            )}
                        </Card>

                        {/* Selection count */}
                        {selectedEnforcementObjectIds.size > 0 && (
                            <MutedText size="sm">
                                {i18n.accessProfiles.selectedCount({count: selectedEnforcementObjectIds.size})}
                            </MutedText>
                        )}

                        {/* Operation selection */}
                        <Stack gap="sm">
                            <Label>{i18n.accessProfiles.operationLabel}</Label>
                            <Select value={selectedOperation} onValueChange={setSelectedOperation}>
                                <SelectTrigger data-testid="select-operation">
                                    <SelectValue placeholder={i18n.accessProfiles.selectOperation}/>
                                </SelectTrigger>
                                <SelectContent>
                                    {operationTypes.map((op) => (
                                        <SelectItem key={op.code} value={op.code}>
                                            {op.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Stack>
                    </Stack>
                    <SaveCancelFooter
                        testIdPrefix="add-binding"
                        onCancel={addBindingDialog.close}
                        saving={addBindingMutation.isPending}
                        disabled={selectedEnforcementObjectIds.size === 0 || !selectedOperation}
                        saveLabel={i18n.accessProfiles.addBindingsButton({count: selectedEnforcementObjectIds.size})}
                        savingLabel={i18n.accessProfiles.adding}
                        saveType="button"
                        onSave={handleAddBinding}
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Binding Confirmation Dialog */}
            <Dialog open={!!deleteBindingId} onOpenChange={(open) => !open && setDeleteBindingId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.accessProfiles.removeBinding}</DialogTitle>
                        <DialogDescription>
                            {i18n.accessProfiles.removeBindingConfirm}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="remove-binding"
                        onCancel={() => setDeleteBindingId(null)}
                        saving={removeBindingMutation.isPending}
                        saveLabel={i18n.accessProfiles.remove}
                        savingLabel={i18n.accessProfiles.removing}
                        destructive
                        saveType="button"
                        onSave={() => deleteBindingId && handleRemoveBinding(deleteBindingId)}
                    />
                </DialogContent>
            </Dialog>

            {/* Activate Profile Dialog */}
            <Dialog open={activateDialog.isOpen} onOpenChange={activateDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.accessProfiles.activateProfile}</DialogTitle>
                        <DialogDescription>
                            {i18n.accessProfiles.activateConfirm({name: profile.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="activate"
                        onCancel={activateDialog.close}
                        saving={activateMutation.isPending}
                        saveLabel={i18n.accessProfiles.activate}
                        savingLabel={i18n.accessProfiles.activating}
                        saveType="button"
                        onSave={() => activateMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>

            {/* Deprecate Profile Dialog */}
            <Dialog open={deprecateDialog.isOpen} onOpenChange={deprecateDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.accessProfiles.deprecateProfile}</DialogTitle>
                        <DialogDescription>
                            {i18n.accessProfiles.deprecateConfirm({name: profile.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="deprecate"
                        onCancel={deprecateDialog.close}
                        saving={deprecateMutation.isPending}
                        saveLabel={i18n.accessProfiles.deprecate}
                        savingLabel={i18n.accessProfiles.deprecating}
                        saveType="button"
                        onSave={() => deprecateMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Profile Dialog */}
            <Dialog open={deleteProfileDialog.isOpen} onOpenChange={deleteProfileDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.accessProfiles.deleteProfile}</DialogTitle>
                        <DialogDescription>
                            {i18n.accessProfiles.deleteConfirm({name: profile.name})}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete-profile"
                        onCancel={deleteProfileDialog.close}
                        saving={deleteProfileMutation.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => deleteProfileMutation.mutate()}
                    />
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
